import React from 'react';
import { useTranslation } from 'react-i18next';
import loaderStyles from '../../../assets/loader.module.css';

interface WordCountControlProps {
  maxWordsPerCaption: number;
  setMaxWordsPerCaption: (value: number) => void;
  isRefetchingCaptions: boolean;
  disabledBtnPendingDownload: boolean;
  onRefetchCaptions: () => void;
}

export const WordCountControl: React.FC<WordCountControlProps> = ({
  maxWordsPerCaption,
  setMaxWordsPerCaption,
  isRefetchingCaptions,
  disabledBtnPendingDownload,
  onRefetchCaptions
}) => {
  const { t } = useTranslation('captionEditor');
  
  const getWordCountRecommendation = (wordCount: number): string => {
    if (wordCount === 1) return t('captionEditor.wordCountRecommendations.vertical');
    if (wordCount >= 2 && wordCount <= 5) return t('captionEditor.wordCountRecommendations.shortForm');
    if (wordCount >= 6 && wordCount <= 10) return t('captionEditor.wordCountRecommendations.social');
    if (wordCount >= 11 && wordCount <= 15) return t('captionEditor.wordCountRecommendations.standard');
    if (wordCount >= 16) return t('captionEditor.wordCountRecommendations.detailed');
    return '';
  };
  
  return (
    <div style={{
      backgroundColor: '#2a2a2a',
      border: '1px solid #444',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '16px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '500',
          color: '#e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          {t('captionEditor.controls.wordsPerCaption.label')}
        </label>
        <span style={{
          backgroundColor: '#8b5cf6',
          color: '#ffffff',
          padding: '4px 12px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {maxWordsPerCaption}
        </span>
      </div>
      
      <input
        type="range"
        min="1"
        max="20"
        value={maxWordsPerCaption}
        onChange={(e) => setMaxWordsPerCaption(Number(e.target.value))}
        style={{
          width: '100%',
          marginBottom: '12px',
          accentColor: '#8b5cf6'
        }}
      />
      
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '11px',
        color: '#888',
        marginBottom: '12px'
      }}>
        <span>{t('captionEditor.controls.wordsPerCaption.minLabel')}</span>
        <span>{t('captionEditor.controls.wordsPerCaption.maxLabel')}</span>
      </div>
      
      <button
        onClick={onRefetchCaptions}
        disabled={isRefetchingCaptions || disabledBtnPendingDownload}
        style={{
          width: '100%',
          padding: '10px',
          borderRadius: '8px',
          border: 'none',
          background: isRefetchingCaptions 
            ? '#444' 
            : 'linear-gradient(135deg, #8b5cf6, #ec4899)',
          color: '#ffffff',
          fontSize: '13px',
          fontWeight: '600',
          cursor: isRefetchingCaptions ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: isRefetchingCaptions ? 0.6 : 1
        }}
      >
        {isRefetchingCaptions ? (
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span className={loaderStyles["lds-roller-min"]}>
              <div></div><div></div><div></div><div></div>
              <div></div><div></div><div></div><div></div>
            </span>
            {t('captionEditor.controls.wordsPerCaption.applying')}
          </span>
        ) : (
          t('captionEditor.controls.wordsPerCaption.applyButton')
        )}
      </button>
      
      <div style={{
        marginTop: '8px',
        fontSize: '11px',
        color: '#888',
        textAlign: 'center'
      }}>
        {getWordCountRecommendation(maxWordsPerCaption)}
      </div>
    </div>
  );
};
